/**
 * TrimsCard — the trims register for a Product Dev record.
 *
 * Trims are everything added to a garment (zips, drawcords, labels, patches,
 * buttons). Each trim can optionally be LINKED to a real inventory SKU so its
 * stock becomes trackable, and carries an image so the factory knows what it
 * is. PO-line + stock-consumption flows are deliberately deferred — this is the
 * list + linkage MVP.
 *
 * Persistence mirrors VariantsCard / AttributesCard: every edit resends the
 * WHOLE trim array through `setTrims` (full-replace reconcile by id). Existing
 * rows carry their `id` so they update in place; the add row omits it to insert.
 * Carrying every field on every resend is the clobber-safe contract — an
 * unrelated edit (rename, link, image) can never drop another field.
 *
 * Image upload reuses the page's single upload chokepoint via the
 * `onUploadImage` prop (see ProductDevDetail): the base64 + size-validation
 * helpers live in the page, so passing a seam beats duplicating them here.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Plus,
  Trash2,
  Image as ImageIcon,
  Upload,
  Link2,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { trpc } from '@/lib/trpc';
import {
  useProductDevTrims,
  useProductDevMutations,
} from '@/features/product-dev/hooks/useProductDev';
import { InlineEdit } from '@/features/product-dev/components/InlineEdit';
import { CardRowsSkeleton } from '@/features/product-dev/components/skeletons';

// The row shape sent to setTrims. `id` is omitted for new inserts; every other
// field rides along on every resend so unrelated edits never clobber it.
type TrimPayloadRow = {
  id?: string;
  name: string;
  sku_id: string | null;
  consumption_per_unit: number | null;
  image_file_id: string | null;
  notes: string | null;
  sort_order: number;
};

export function TrimsCard({
  productDevId,
  canEdit,
  onUploadImage,
}: {
  productDevId: string;
  canEdit: boolean;
  /** Uploads an image via the page's chokepoint; resolves the new file id (or
   *  null on validation failure / error — the page owns the toast). */
  onUploadImage: (file: File) => Promise<string | null>;
}) {
  const { t } = useTranslation('productDev');
  const trimsQuery = useProductDevTrims(productDevId);
  const { setTrims } = useProductDevMutations(productDevId);

  const rows = useMemo(() => trimsQuery.data ?? [], [trimsQuery.data]);
  const hasLinked = rows.some((r) => r.sku_id);

  // Freshest rows at CALL time, not render time. setTrims is a full-replace
  // reconcile that prunes anything absent — a persist built from a stale
  // render-time snapshot (e.g. the multi-second image-upload window, during
  // which another trim was added or edited) would silently delete/revert the
  // concurrent change (review finding).
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [addName, setAddName] = useState('');
  const addNameRef = useRef('');

  // Rebuild the full payload from the freshest server rows, applying `mutate`.
  const persist = async (mutate: (base: TrimPayloadRow[]) => TrimPayloadRow[]) => {
    const base: TrimPayloadRow[] = rowsRef.current.map((r, i) => ({
      id: r.id,
      name: r.name,
      sku_id: r.sku_id,
      consumption_per_unit: r.consumption_per_unit,
      image_file_id: r.image_file_id,
      notes: r.notes,
      sort_order: i,
    }));
    const next = mutate(base).map((r, i) => ({ ...r, sort_order: i }));
    try {
      await setTrims.mutateAsync({ productDevId, trims: next });
    } catch (err) {
      toast({
        title: t('trims.toast.saveFailed'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const patchTrim = (id: string, patch: Partial<Omit<TrimPayloadRow, 'id' | 'sort_order'>>) =>
    persist((base) => base.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeTrim = (id: string) => persist((base) => base.filter((r) => r.id !== id));

  const addTrim = (name: string) =>
    persist((base) => [
      ...base,
      {
        name,
        sku_id: null,
        consumption_per_unit: null,
        image_file_id: null,
        notes: null,
        sort_order: base.length,
      },
    ]);

  const commitAdd = () => {
    const name = addNameRef.current.trim();
    if (!name || setTrims.isPending) return;
    // Clear synchronously (ref + state) so a blur firing right after Enter
    // reads an empty value and no-ops instead of double-adding.
    addNameRef.current = '';
    setAddName('');
    void addTrim(name);
  };

  const handleImagePick = async (id: string, file: File) => {
    setUploadingId(id);
    try {
      const fileId = await onUploadImage(file);
      if (fileId) await patchTrim(id, { image_file_id: fileId });
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">{t('trims.title')}</CardTitle>
          {rows.length > 0 && (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {rows.length}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {trimsQuery.isLoading ? (
          <CardRowsSkeleton rows={2} />
        ) : trimsQuery.error ? (
          <p className="text-sm text-destructive">{trimsQuery.error.message}</p>
        ) : (
          <>
            {rows.length === 0 && !canEdit ? (
              <p className="text-xs text-muted-foreground">{t('trims.empty')}</p>
            ) : rows.length === 0 ? (
              <p className="text-xs text-muted-foreground pb-1">{t('trims.empty')}</p>
            ) : (
              rows.map((tr) => (
                <div
                  key={tr.id}
                  className="group flex items-start gap-3 py-2 border-b last:border-b-0"
                >
                  <TrimImageCell
                    imageUrl={tr.image_url}
                    altText={tr.name}
                    canEdit={canEdit}
                    uploading={uploadingId === tr.id}
                    onPick={(file) => void handleImagePick(tr.id, file)}
                  />
                  <div className="flex-1 min-w-0 space-y-1">
                    <InlineEdit
                      value={tr.name}
                      onSave={(v) => patchTrim(tr.id, { name: v?.trim() ? v.trim() : tr.name })}
                      placeholder={t('trims.namePlaceholder')}
                      readOnly={!canEdit}
                      displayClassName="text-sm font-medium"
                      maxLength={120}
                    />
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <TrimSkuPicker
                        skuId={tr.sku_id}
                        skuCode={tr.sku_code}
                        readOnly={!canEdit}
                        onSave={(next) => patchTrim(tr.id, { sku_id: next })}
                      />
                      {/* Qty per unit — labelled so the number is never a
                          mystery "Q…" abbreviation; the field shows its value
                          (or a dash) untruncated at a comfortable width. */}
                      <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="shrink-0">{t('trims.consumption.label')}</span>
                        <span className="inline-block min-w-[3.25rem]">
                          <InlineEdit
                            type="number"
                            value={
                              tr.consumption_per_unit == null
                                ? null
                                : String(tr.consumption_per_unit)
                            }
                            onSave={(v) =>
                              patchTrim(tr.id, {
                                consumption_per_unit: v == null ? null : Number(v),
                              })
                            }
                            placeholder={t('trims.consumption.placeholder')}
                            readOnly={!canEdit}
                            emptyVariant="dash"
                            min={0}
                            step="0.01"
                            displayClassName="text-xs tabular-nums"
                            inputClassName="tabular-nums"
                          />
                        </span>
                        {tr.consumption_per_unit != null && (
                          <span className="shrink-0">{t('trims.consumption.perUnit')}</span>
                        )}
                      </div>
                    </div>
                    <InlineEdit
                      value={tr.notes}
                      onSave={(v) => patchTrim(tr.id, { notes: v })}
                      placeholder={t('trims.notesPlaceholder')}
                      readOnly={!canEdit}
                      displayClassName="text-xs text-muted-foreground"
                      maxLength={2000}
                    />
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => void removeTrim(tr.id)}
                      className="text-muted-foreground hover:text-destructive p-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                      aria-label={t('trims.aria.remove')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))
            )}

            {canEdit && (
              <div className="flex items-center gap-3 pt-2">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-dashed border-muted-foreground/30 text-muted-foreground/50">
                  <Plus className="h-4 w-4" />
                </span>
                <Input
                  value={addName}
                  onChange={(e) => {
                    addNameRef.current = e.target.value;
                    setAddName(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitAdd();
                    }
                  }}
                  onBlur={commitAdd}
                  placeholder={t('trims.addPlaceholder')}
                  className="h-8 text-sm"
                  maxLength={120}
                  disabled={setTrims.isPending}
                />
              </div>
            )}

            {hasLinked && (
              <p className="pt-2 text-[11px] text-muted-foreground">
                {t('trims.linkedFootnote')}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// TrimImageCell — 40×40 image thumb; muted placeholder tile when none.
// Same visual family as the list thumbnails (bg-muted + centred icon).
// ----------------------------------------------------------------------------

function TrimImageCell({
  imageUrl,
  altText,
  canEdit,
  uploading,
  onPick,
}: {
  imageUrl: string | null;
  altText: string;
  canEdit: boolean;
  uploading: boolean;
  onPick: (file: File) => void;
}) {
  const { t } = useTranslation('productDev');
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Reset the decode-error flag whenever the source changes so a replaced
  // image gets a fresh chance to load (undecodable HEIC / dead URL → tile).
  const [imgError, setImgError] = useState(false);
  useEffect(() => setImgError(false), [imageUrl]);

  return (
    <div className="relative shrink-0">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPick(file);
          if (e.target) e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => canEdit && !uploading && inputRef.current?.click()}
        disabled={!canEdit || uploading}
        className={cn(
          'group/timg relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-md border border-border bg-muted',
          canEdit && 'cursor-pointer hover:border-threadheads-purple/40 transition-colors',
          !canEdit && 'cursor-default',
        )}
        aria-label={imageUrl ? t('trims.aria.replaceImage') : t('trims.aria.uploadImage')}
      >
        {imageUrl && !imgError ? (
          <img
            src={imageUrl}
            alt={altText || t('trims.imageAltFallback')}
            loading="lazy"
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <ImageIcon className="h-4 w-4 text-muted-foreground/50" aria-hidden="true" />
        )}
        {canEdit && (
          <span
            className={cn(
              'absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity',
              uploading ? 'opacity-100' : 'opacity-0 group-hover/timg:opacity-100',
            )}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 text-white animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5 text-white" />
            )}
          </span>
        )}
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// TrimSkuPicker — compact type-to-search combobox linking a trim to an
// inventory SKU. Mirrors InlineProductSearch's server-side search + cmdk
// palette, pared down for the inline row context. Clearable.
// ----------------------------------------------------------------------------

function TrimSkuPicker({
  skuId,
  skuCode,
  readOnly,
  onSave,
}: {
  skuId: string | null;
  skuCode: string | null;
  readOnly?: boolean;
  onSave: (next: string | null) => Promise<void> | void;
}) {
  const { t } = useTranslation('productDev');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [term, setTerm] = useState('');

  // Scope the picker to trim SKUs (skus.is_trim = true). The shared
  // `useSearchSkus` hook doesn't take options, so query directly here with
  // `trimsOnly: true` — same enabled/staleTime/placeholder contract as the hook.
  const trimmedTerm = term.trim();
  const searchQuery = trpc.productDev.searchSkus.useQuery(
    { query: trimmedTerm, trimsOnly: true },
    {
      enabled: trimmedTerm.length >= 2,
      staleTime: 60_000,
      placeholderData: (prev) => prev,
    },
  );
  const options = searchQuery.data ?? [];
  const tooShort = trimmedTerm.length < 2;

  const commit = async (next: string | null) => {
    if (next === (skuId ?? null)) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setOpen(false);
    } catch {
      // The parent's persist() already surfaces a toast; keep the popover open.
    } finally {
      setSaving(false);
    }
  };

  // Read-only linked chip: no picker, just the code (or nothing when unlinked).
  if (readOnly) {
    return skuId ? (
      <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
        <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />
        {skuCode ?? t('trims.sku.linked')}
      </span>
    ) : null;
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (saving) return;
        setOpen(o);
        if (o) setTerm('');
      }}
    >
      <PopoverTrigger asChild>
        {skuId ? (
          <span className="inline-flex items-center gap-1 rounded-md border border-threadheads-purple/30 bg-threadheads-purple/10 pl-1.5 pr-0.5 py-0.5 font-mono text-[10px] text-threadheads-purple">
            <button
              type="button"
              className="inline-flex items-center gap-1 hover:underline"
              aria-label={t('trims.sku.aria.change')}
            >
              <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />
              {skuCode ?? t('trims.sku.linked')}
            </button>
            {saving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void commit(null);
                }}
                className="rounded-full p-0.5 hover:bg-threadheads-purple/20"
                aria-label={t('trims.sku.aria.clear')}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ) : (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-muted-foreground/30 px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:border-threadheads-purple/40 hover:text-threadheads-purple"
          >
            <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t('trims.sku.link')}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        {/* Server-side search — cmdk's own filtering stays off. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('trims.sku.search')}
            value={term}
            onValueChange={setTerm}
          />
          <CommandList>
            <CommandEmpty>
              {tooShort
                ? t('trims.sku.typeToSearch')
                : searchQuery.isFetching
                  ? t('trims.sku.searching')
                  : t('trims.sku.noTrimResults')}
            </CommandEmpty>
            {skuId && (
              <CommandItem
                value="__unlink__"
                onSelect={() => void commit(null)}
                className="text-muted-foreground italic"
              >
                {t('trims.sku.unlink')}
              </CommandItem>
            )}
            {options.map((opt) => (
              <CommandItem
                key={opt.id}
                value={opt.id}
                onSelect={() => void commit(opt.id)}
                className="gap-2"
              >
                <span className="font-mono text-xs shrink-0">{opt.sku}</span>
                {opt.name && (
                  <span className="flex-1 min-w-0 truncate text-muted-foreground">
                    {opt.name}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
